/**
 * Seal Subscriptions — price display helpers (sealsubs:price_update).
 * Used on product page and quick-add drawer.
 */
(function () {
  function sealValueToCents(raw) {
    if (raw == null || raw === '') return null;
    const str = String(raw).replace(/\s/g, '');
    if (/^\d{1,14}$/.test(str)) return parseInt(str, 10);
    return null;
  }

  function formatThemeMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      const fmt = window.theme?.settings?.shopMoneyFormat || '{{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    try {
      const code = window.theme?.settings?.shopCurrency || 'EUR';
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: code
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function sealPriceToDisplayHtml(raw) {
    if (raw == null || raw === '') return '';
    const str = String(raw).trim();
    if (str.includes('<')) return str;
    const cents = sealValueToCents(str);
    if (cents != null) return formatThemeMoney(cents);
    return str;
  }

  function sealCompareShouldShow(detail) {
    if (detail.compareAtPrice == null || detail.compareAtPrice === '') return false;
    const p = sealValueToCents(detail.price);
    const c = sealValueToCents(detail.compareAtPrice);
    if (p != null && c != null) {
      if (p === c) return false;
      return c > p;
    }
    const ps = String(detail.price ?? '').trim();
    const cs = String(detail.compareAtPrice ?? '').trim();
    if (ps && cs && ps === cs) return false;
    return true;
  }

  /**
   * Parse a displayed money string (e.g. "27,62 €") into integer cents.
   * @param {string|null|undefined} text
   * @returns {number|null}
   */
  function parseDisplayedMoneyToCents(text) {
    if (text == null || text === '') return null;
    const cleaned = String(text).replace(/[^\d,.-]/g, '').trim();
    if (!cleaned) return null;

    let normalized = cleaned;
    if (normalized.includes(',') && normalized.includes('.')) {
      // 1.234,56 → 1234.56
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else if (normalized.includes(',')) {
      normalized = normalized.replace(',', '.');
    }

    const value = parseFloat(normalized);
    if (Number.isNaN(value)) return null;
    return Math.round(value * 100);
  }

  /**
   * Resolve unit price in cents from Seal detail or DOM ratio.
   * @param {object} detail
   * @param {number|null} priceCents
   * @param {Element} priceWrap
   * @returns {number|null}
   */
  function resolveUnitPriceCents(detail, priceCents, priceWrap) {
    const fromSeal = detail?.pricePerUnit?.pricePerUnit;
    if (fromSeal != null && fromSeal !== '') {
      const cents = sealValueToCents(fromSeal);
      if (cents != null) return cents;
      const asInt = parseInt(fromSeal, 10);
      if (!Number.isNaN(asInt)) return asInt;
    }

    if (priceCents == null || priceCents <= 0) return null;

    const currentPriceEl = priceWrap.querySelector('.price__current');
    const unitPriceEl = priceWrap.querySelector('.unit-price:not(.unit-price--was) .unit-price__price');
    const oldPriceCents = parseDisplayedMoneyToCents(currentPriceEl?.textContent);
    const oldUnitCents = parseDisplayedMoneyToCents(unitPriceEl?.textContent);

    if (oldPriceCents && oldUnitCents) {
      return Math.round((priceCents * oldUnitCents) / oldPriceCents);
    }

    return null;
  }

  /**
   * @param {Element} unitEl
   * @param {number} unitCents
   * @param {boolean} taxesIncluded
   */
  function setUnitPriceAmount(unitEl, unitCents, taxesIncluded) {
    const priceEl = unitEl.querySelector('.unit-price__price');
    if (!priceEl) return;

    let html = formatThemeMoney(unitCents);
    if (taxesIncluded && theme?.strings?.taxesIncluded) {
      html += ' ' + theme.strings.taxesIncluded;
    }
    priceEl.innerHTML = html;
  }

  /**
   * @param {Element} priceWrap - `.price` container
   * @param {object} detail - sealsubs:price_update detail
   */
  function applySealPriceUpdate(priceWrap, detail) {
    if (!priceWrap || detail?.price == null) return;

    const showCompare = sealCompareShouldShow(detail);
    const taxesIncluded = priceWrap.classList.contains('price--taxes-included');
    const priceCents = sealValueToCents(detail.price);
    const compareCents = sealValueToCents(detail.compareAtPrice);
    const unitCents = resolveUnitPriceCents(detail, priceCents, priceWrap);

    const priceCurrentEl = priceWrap.querySelector('.price__current');
    const priceWasEl = priceWrap.querySelector('.price__was');
    const unitPriceEl = priceWrap.querySelector('.unit-price:not(.unit-price--was)');
    const compareUnitEl = priceWrap.querySelector('.unit-price--was');

    if (priceCurrentEl) {
      priceCurrentEl.innerHTML = sealPriceToDisplayHtml(detail.price);
    }
    if (priceWasEl) {
      if (showCompare) {
        let comparePriceText = sealPriceToDisplayHtml(detail.compareAtPrice);
        if (taxesIncluded && theme?.strings?.taxesIncluded) {
          comparePriceText += ' ' + theme.strings.taxesIncluded;
        }
        priceWasEl.innerHTML = comparePriceText;
        priceWasEl.hidden = false;
      } else {
        priceWasEl.innerHTML = '';
        priceWasEl.hidden = true;
      }
    }

    if (unitPriceEl && !unitPriceEl.hidden && unitCents != null) {
      setUnitPriceAmount(unitPriceEl, unitCents, taxesIncluded);

      if (detail.pricePerUnit?.unit) {
        const unitLabelEl = unitPriceEl.querySelector('.unit-price__unit');
        if (unitLabelEl && !unitLabelEl.textContent.trim()) {
          unitLabelEl.textContent = detail.pricePerUnit.unit;
        }
      }
    }

    if (compareUnitEl) {
      const compareUnitCents =
        showCompare && compareCents != null && unitCents != null && priceCents > 0
          ? Math.round((compareCents * unitCents) / priceCents)
          : null;

      if (compareUnitCents != null) {
        if (!compareUnitEl.querySelector('.unit-price__price')) {
          const unitLabel =
            unitPriceEl?.querySelector('.unit-price__unit')?.textContent ||
            detail.pricePerUnit?.unit ||
            '';
          compareUnitEl.innerHTML = `
            <span class="unit-price__price"></span>
            <span class="unit-price__separator"> / </span>
            <span class="unit-price__unit">${unitLabel}</span>
          `;
        } else {
          const unitLabelEl = compareUnitEl.querySelector('.unit-price__unit');
          const sourceLabel = unitPriceEl?.querySelector('.unit-price__unit')?.textContent;
          if (unitLabelEl && sourceLabel) unitLabelEl.textContent = sourceLabel;
        }

        setUnitPriceAmount(compareUnitEl, compareUnitCents, taxesIncluded);
        compareUnitEl.hidden = false;
      } else {
        compareUnitEl.hidden = true;
      }
    }

    priceWrap.classList.toggle('price--on-sale', showCompare);
  }

  window.theme = window.theme || {};
  window.theme.sealSubscriptionPrice = {
    sealValueToCents,
    sealPriceToDisplayHtml,
    sealCompareShouldShow,
    applySealPriceUpdate
  };
})();
